import React from "react";
import { Package, AlertTriangle, Calendar, Barcode, CheckCircle } from "lucide-react";
import { PurchaseOrder, Product } from "src/types";
import {
  ResolveGXOErrorFormData,
  ResolveGXOErrorFormDataKey,
  ResolveGXOErrorFormAction,
  ProductBarcodeUpdate
} from "src/types/forms/purchaseOrders/resolveGXOErrorForm";
import { ValidationErrors } from "src/errors";
import { useScrollOnMessage } from "src/hooks";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { TextInputBox, DateInputBox } from "src/components/ui/inputs";
import { Table } from "src/components";
import { ProductColumnHeader } from "src/enums";
import { TableColumn } from "src/types";

interface ResolveGXOErrorFormProps {
  formData: ResolveGXOErrorFormData;
  purchaseOrder?: PurchaseOrder;
  productBarcodeUpdates: ProductBarcodeUpdate[];
  validationErrors?: ValidationErrors<ResolveGXOErrorFormDataKey>;
  formLvlErrorMessage: string;
  successMessage: string;
  isFormComplete: boolean;
  handleInputChange: (field: ResolveGXOErrorFormDataKey, value: string) => void;
  handleBarcodeChange: (productId: string, newBarcode: string) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  setAction: (action: ResolveGXOErrorFormAction) => void;
  isSubmitting: boolean;
}

const ResolveGXOErrorForm: React.FC<ResolveGXOErrorFormProps> = ({
  formData,
  purchaseOrder,
  productBarcodeUpdates,
  validationErrors,
  formLvlErrorMessage,
  successMessage,
  isFormComplete,
  handleInputChange,
  handleBarcodeChange,
  handleSubmit,
  setAction,
  isSubmitting
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const productBarcodeTableColumns: TableColumn<ProductColumnHeader, Product>[] = [
    {
      header: ProductColumnHeader.ID,
      column: (row: Product) => <span className="font-medium text-neutral-900">{row.id}</span>,
      styles: { minWidth: "140px", width: "140px" }
    },
    {
      header: ProductColumnHeader.NAME,
      column: (row: Product) => <span className="text-neutral-900">{row.name}</span>,
      styles: { minWidth: "250px" }
    },
    {
      header: ProductColumnHeader.SKU,
      column: (row: Product) => <span className="text-neutral-700">{row.skuName}</span>,
      styles: { minWidth: "140px", width: "140px" }
    },
    {
      header: ProductColumnHeader.ORDER_QUANTITY,
      column: (row: Product) => <span className="font-semibold text-neutral-900">{row.quantity.toString()}</span>,
      styles: { minWidth: "120px", width: "120px", textAlign: "center" }
    },
    {
      header: ProductColumnHeader.BARCODE,
      column: (row: Product) => <span className="font-mono text-neutral-900">{row.barcode}</span>,
      styles: { minWidth: "140px", width: "140px" }
    },
    {
      header: ProductColumnHeader.NEW_BARCODE,
      column: (row: Product) => (
        <input
          id={`barcode-${row.id}`}
          value={(productBarcodeUpdates.find((u) => u.productId === row.id)?.barcode ?? "").toString()}
          onChange={(e) => handleBarcodeChange(row.id, e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent w-full"
          placeholder="Enter new barcode"
        />
      ),
      styles: { minWidth: "180px", width: "180px" }
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Resolve GXO Error</h1>
            <p className="text-gray-300 mt-2 text-lg">Update product barcodes and resend purchase orders to GXO</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Search Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Purchase Order Search
                </h3>
                <TextInputBox
                  id="purchase-order-id"
                  placeholder="Purchase Order ID"
                  onChange={(value) => {
                    handleInputChange("purchaseOrderId", value.toUpperCase());
                  }}
                  value={formData.purchaseOrderId}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderId")} />
              </div>

              {!purchaseOrder && (
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                  disabled={!isFormComplete}
                  onClick={() => setAction("FETCH")}
                  type="submit"
                >
                  <Package className="w-4 h-4" />
                  <span>Search</span>
                </button>
              )}

              {purchaseOrder && (
                <>
                  {/* Purchase Order Details Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Package className="w-4 h-4 mr-2" />
                      Purchase Order Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Vendor</p>
                        <p className="text-sm font-medium text-gray-900">{purchaseOrder.vendorName}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                        <p className="text-sm font-medium text-gray-900">{purchaseOrder.confirmationStatus}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">ASN</p>
                        <p className="text-sm font-medium text-gray-900">{purchaseOrder.asn}</p>
                      </div>
                    </div>
                  </div>

                  {/* GXO Error Details Section */}
                  {purchaseOrder.gxoError && (
                    <div className="border border-error-200 rounded-2xl p-5 bg-error-50">
                      <h3 className="text-sm font-semibold text-error-800 mb-4 tracking-wide uppercase flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        GXO Error Details
                      </h3>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Error</p>
                          <p className="text-sm font-medium text-error-700">
                            {purchaseOrder.gxoError.message.split(":")[0]}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Timestamp</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(purchaseOrder.gxoError.timestamp).toLocaleDateString()}
                          </p>
                        </div>
                        {purchaseOrder.gxoError.details.length > 0 && (
                          <div className="bg-white rounded-lg p-4">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Details</p>
                            <ul className="list-disc list-inside space-y-1">
                              {purchaseOrder.gxoError.details.map((detail, index) => (
                                <li key={index} className="text-sm text-gray-700">
                                  {detail}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Update Product Barcodes Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Barcode className="w-4 h-4 mr-2" />
                      Update Product Barcodes
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Please update the barcodes for products that caused GXO errors
                    </p>
                    <Table<ProductColumnHeader, Product>
                      data={purchaseOrder.products || []}
                      columns={productBarcodeTableColumns}
                    />
                  </div>

                  {/* Estimated Delivery Date Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Delivery Date
                    </h3>
                    <DateInputBox
                      id="estimated-delivery-date"
                      label="Estimated Delivery Date"
                      onChange={(value) => {
                        handleInputChange("estimatedDeliveryDate", value);
                      }}
                      value={formData.estimatedDeliveryDate}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("estimatedDeliveryDate")}
                    />
                  </div>

                  {/* Form Level Error */}
                  <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

                  {/* Success Message */}
                  {successMessage && (
                    <div className="border border-success-200 rounded-2xl p-5 bg-success-50">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-success-600" />
                        <p className="text-sm font-medium text-success-800">{successMessage}</p>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                    disabled={isSubmitting}
                    onClick={() => setAction("SUBMIT")}
                    type="submit"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>{isSubmitting ? "Updating..." : "Update Barcodes & Resend to GXO"}</span>
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { ResolveGXOErrorForm };
